/**
 * Create-Form runner close-result decoding (#20312).
 *
 * A runner dialog closes with `{ createdEntries, submission }` on a genuine completion and with
 * `undefined` on cancel/Escape (`create-form-modal.component.ts` `confirmClose()`). Every caller
 * that wants the entry the runner actually wrote has to read `createdEntries` correctly, so the
 * rule lives here once instead of being re-derived per caller.
 *
 * Kept dependency-free (no Angular) so it is directly unit-testable — same pattern as
 * `document-card-status.ts` and `option-badge.util.ts`.
 */
import type { Entry } from 'app/features/admin/modules/modules.model';

/** The close-result shape shared by the wizard and flat-form runner paths. */
export interface CreateRunnerCloseResult {
  createdEntries?: Record<string, Entry>;
}

/**
 * Resolve the entry a runner wrote in `targetModuleId` from its `createdEntries` map.
 *
 * `createdEntries` is NOT a single-purpose map, and it is NOT ordered target-entry-first: the
 * wizard fills it while the user advances — an action-carrying step's created entry is keyed by
 * the STEP key (`wizard.component.ts` `#executeStepAction`) — and only appends the completion
 * action's entry, keyed by `interfaceActionId`, at the very end (`wizard.component.ts`
 * `runCompletionAction`). Taking the first value therefore returns whatever an earlier step
 * created (e.g. the Uploads-bak entry behind a file field), not the target-module record.
 *
 * Matching on `moduleId` is reliable because `runActionCreate` stamps every created entry with its
 * target module id for exactly this purpose ("so consumers … can match the created entry to a
 * specific module across a multi-module wizard", `interface-action-runner.service.ts`). The
 * single-entry fallback exists only for paths that do NOT stamp it — a lone entry that carries a
 * different moduleId is a mismatch, not a fallback candidate.
 *
 * Returns null when nothing was created (cancel closes with `undefined`; a wizard whose steps
 * carry no action completes with an empty but truthy `{}`), and when several entries were created
 * and none belongs to the target module — naming the wrong record is worse than staying silent.
 */
export function resolveCreatedEntry(
  createdEntries: Record<string, Entry> | null | undefined,
  targetModuleId: string | undefined
): Entry | null {
  const entries = Object.values(createdEntries ?? {});
  if (!entries.length) {
    return null;
  }
  // Guard on a known target: without it, `entry.moduleId === undefined` would "match" any entry
  // that happens to carry no moduleId, which is an accident rather than a resolution.
  const matched = targetModuleId ? entries.find(entry => entry.moduleId === targetModuleId) : undefined;
  if (matched) {
    return matched;
  }
  // The fallback is for an UNSTAMPED entry only. A lone entry that does carry a moduleId has
  // already told us which module it belongs to, and it is not this one — returning it anyway
  // would announce (or, in the autocomplete, select) an entry from another module.
  const single = entries.length === 1 ? entries[0] : undefined;
  return single && !single.moduleId ? single : null;
}
